import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { match, RouteComponentProps } from 'react-router-dom';

import { withTranslation, WithTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import Chip from '@material-ui/core/Chip';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas } from '../../../local-types';
import Listing from '../../../components/listing';
import { Dialog } from '../../../components/dialog';
import { TooltipButton } from '../../../components/tooltip-button';
// Modules
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    actions as prebookActions,
    selectors as prebookSelectors,
    types as prebookTypes,
} from '../../../redux/reducers/prebook';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { PrebookGroupCreationPayload, PrebookById, PrebookPlanning, TParams } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import DateUtils from '../../../utils/dateUtils';
import FormUtils from '../../../utils/forms';
import StringUtils from '../../../utils/stringUtils';
import { paths } from '../../paths';
import { clsx } from '../../../utils/htmlUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';

const queryString = require('query-string');

interface PrebooksAddPlanningPageProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    match: match<TParams>;
    prebooks: PrebookById;
    crudPrebookError: ErrorPayload;
    isSavingPrebook: boolean;
    getPrebook: (id: any) => any;
    updatePrebook: (id: any, payload: PrebookGroupCreationPayload) => any;
}

const PrebooksAddPlanningPage = (props: PrebooksAddPlanningPageProps): JSX.Element => {
    const params = queryString.parse(props.location.search);
    const { t, match, globalClasses, prebooks, getPrebook, updatePrebook, crudPrebookError, isSavingPrebook } = props;

    const [formInit] = React.useState<any>({
        start: null,
        end: null,
        delay: null,
        duration: null,
        maxPrebookPerDriver: null,
    });
    const [formData, setFormData] = React.useState<any>({
        // start: '05:43',
        // end: '10:23',
        // delay: 23,
        // duration: 34,
        // maxPrebookPerDriver: 5,
        start: null,
        end: null,
        delay: null,
        duration: null,
        maxPrebookPerDriver: null,
        days: [
            { id: '1', short: 'mon', value: true },
            { id: '2', short: 'tue', value: true },
            { id: '3', short: 'wed', value: true },
            { id: '4', short: 'thu', value: true },
            { id: '5', short: 'fri', value: true },
            { id: '6', short: 'sat', value: false },
            { id: '0', short: 'sun', value: false },
        ],
    });

    const [planning, setPlanning] = React.useState<PrebookPlanning>({} as PrebookPlanning);

    useEffect(() => {
        if (match.params.id) {
            getPrebook(match.params.id);
        }
    }, [match.params.id, getPrebook]);

    //TODO: pas faire le setFormData sans recheck si c'est nécessaire
    useEffect(() => {
        const prebook = prebooks[match.params.id];
        if (prebook) {
            const planning = prebook.plannings.find((p) => p.id === params.planning_id);
            if (planning) {
                formatPayloadTime(planning, false);
                setPlanning(planning);

                if (planning.time_slots?.weekDays?.length > 0) {
                    const next = { ...formData };

                    next.days.forEach((d) => {
                        d.value = planning.time_slots.weekDays.includes(d.id);
                    });

                    setFormData(next);
                }
            } else {
                if (!prebook.plannings) {
                    prebook.plannings = [];
                }
                const newPlanning: PrebookPlanning = {
                    time_slots: {
                        hours: [],
                        weekDays: [],
                    },
                };
                prebook.plannings.push(newPlanning);
                setPlanning(newPlanning);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prebooks, params.planning_id, match.params.id]);

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    const handleChipsActivate = (index) => (e) => {
        e.stopPropagation();
        e.preventDefault();
        const next = { ...formData };
        next.days[index].value = !next.days[index].value;
        setFormData(next);
    };

    const handleChangeHours = (event) => {
        setFormData(FormUtils.nextSubState(event, formData));
    };

    const handleDelete = (index) => (e) => {
        const next = { ...planning };
        next.time_slots.hours.splice(index, 1);
        setPlanning(next);
    };

    const handleSubmitHours = (e) => {
        e.preventDefault();

        const next = { ...planning };
        if (!next?.time_slots) {
            next.time_slots = { hours: [], weekDays: [] };
        }
        if (!next.time_slots?.hours) {
            next.time_slots.hours = [];
        }

        const payload = {
            maxPrebookPerDriver: formData.maxPrebookPerDriver,
            rideDate: {
                end: formData.end,
                start: formData.start,
            },
            visibility: {
                delay: formData.delay,
                duration: formData.duration,
            },
        };

        next.time_slots.hours.push(payload);

        setPlanning(next);
        setFormData(Object.assign({ ...formData }, formInit));
    };

    const formatPayloadTime = (plan: PrebookPlanning, sendMode = true): void => {
        if (plan?.time_slots?.hours) {
            plan.time_slots.hours.forEach((h) => {
                if (h?.visibility?.delay) {
                    h.visibility.delay = sendMode ? h.visibility.delay * 60 : h.visibility.delay / 60;
                }
                if (h?.visibility?.duration) {
                    h.visibility.duration = sendMode ? h.visibility.duration * 60 : h.visibility.duration / 60;
                }
            });
        }
    };

    const handleSubmit = (formData): void => {
        // console.log('Submitting', formData);

        // Set the new days
        const weekDays = [];
        formData.days.forEach((d) => {
            if (d.value) {
                weekDays.push(d.id);
            }
        });
        planning.time_slots.weekDays = weekDays;

        const id = match.params.id;
        const prebook = prebooks[id];
        const payload = { ...prebook };
        const plan = payload.plannings.find((p) => p.id === planning.id);
        formatPayloadTime(plan);
        // console.log(payload, planning);

        updatePrebook(id, payload);
    };

    const handleDeletePlanning = (): void => {
        const id = match.params.id;
        const prebook = prebooks[id];
        if (prebook && planning) {
            const index = prebook.plannings.findIndex((p) => p === planning);
            prebook.plannings.splice(index, 1);
            const payload = { ...prebook };
            updatePrebook(id, payload);
        }
    };

    const generateInputs = (): FormDatas[] => {
        const inputs: FormDatas[] = [
            {
                title: t('hours.form.title'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        id: 'dayshours',
                        label: t('hours.form.title'),
                        type: 'create',
                        customView: (val, handleChange) => {
                            return (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <h2>{t('details.how')}</h2>
                                            <p style={{ whiteSpace: 'pre-wrap' }}>{t('details.how_details')}</p>
                                        </div>
                                        <h2 style={{ display: 'flex' }}>{t('details.ride')}</h2>
                                        <div style={{ display: 'flex', margin: 'auto', marginBottom: 16 }}>
                                            <b>{t('hours.lock_days_hint')}</b>
                                        </div>
                                        <div style={{ display: 'flex', margin: 'auto', marginBottom: 30 }}>
                                            {DateUtils.generateDays(t).map((d, index) => {
                                                const activated = formData.days[index].value;
                                                return (
                                                    <Chip
                                                        key={d.id}
                                                        icon={null}
                                                        color={activated ? 'secondary' : 'default'}
                                                        label={d.label}
                                                        disabled={
                                                            planning?.time_slots?.hours &&
                                                            planning.time_slots.hours.length > 0
                                                        }
                                                        onClick={handleChipsActivate(index)}
                                                        style={{ marginRight: 8 }}
                                                        // className={classes.chip}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div style={{ display: 'flex', margin: 'auto', marginBottom: 16 }}>
                                            <b>{t('hours.lock_hours_hint')}</b>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 20,
                                            }}
                                        >
                                            <span style={{ margin: '8' }}>{t('common:from')}</span>
                                            <FormControl key={'start'} margin="dense">
                                                <Input
                                                    id={'start'}
                                                    type={'time'}
                                                    inputProps={{ form: 'form_add_hours' }}
                                                    placeholder={'_:_'}
                                                    value={formData.start ?? ''}
                                                    name={`start`}
                                                    required={true}
                                                    onChange={handleChangeHours}
                                                />
                                            </FormControl>
                                            <span style={{ margin: '8' }}>{t('common:hour_to')}</span>
                                            <FormControl key={'end'} margin="dense">
                                                <Input
                                                    id={'end'}
                                                    type={'time'}
                                                    inputProps={{ form: 'form_add_hours' }}
                                                    placeholder={'_:_'}
                                                    value={formData.end ?? ''}
                                                    name={`end`}
                                                    required={true}
                                                    onChange={handleChangeHours}
                                                />
                                            </FormControl>
                                        </div>
                                        <h2 style={{ display: 'flex' }}>
                                            {t('details.hours')}
                                            <div style={{ fontSize: '0.5em', alignSelf: 'center', marginLeft: 8 }}>
                                                {t('details.hours_add')}
                                            </div>
                                        </h2>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 20,
                                            }}
                                        >
                                            <span style={{ margin: '8' }}>{t('hours.form.delay')}</span>
                                            <FormControl key={'delay'} margin="dense">
                                                <Input
                                                    id={'delay'}
                                                    type={'number'}
                                                    inputProps={{ form: 'form_add_hours', step: 1 }}
                                                    placeholder={'5'}
                                                    value={formData.delay ?? ''}
                                                    name={`delay`}
                                                    required={true}
                                                    onChange={handleChangeHours}
                                                />
                                            </FormControl>
                                            <span style={{ margin: '8' }}>{t('hours.form.duration')}</span>
                                            <FormControl key={'duration'} margin="dense">
                                                <Input
                                                    id={'duration'}
                                                    type={'number'}
                                                    inputProps={{ form: 'form_add_hours', step: 1 }}
                                                    placeholder={'5'}
                                                    value={formData.duration ?? ''}
                                                    name={`duration`}
                                                    required={true}
                                                    onChange={handleChangeHours}
                                                />
                                            </FormControl>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 50,
                                            }}
                                        >
                                            <span style={{ margin: '8' }}>{t('hours.form.maxPrebookPerDriver')}</span>
                                            <FormControl key={'maxPrebookPerDriver'} margin="dense">
                                                <Input
                                                    id={'maxPrebookPerDriver'}
                                                    type={'number'}
                                                    inputProps={{ form: 'form_add_hours', step: 1 }}
                                                    placeholder={'5'}
                                                    value={formData.maxPrebookPerDriver ?? ''}
                                                    name={`maxPrebookPerDriver`}
                                                    required={true}
                                                    onChange={handleChangeHours}
                                                />
                                            </FormControl>
                                            <TooltipButton title={t('common:add')}>
                                                <Button
                                                    aria-label="add_hours"
                                                    type="submit"
                                                    variant="contained"
                                                    form="form_add_hours"
                                                    color={
                                                        formData.start &&
                                                        formData.end &&
                                                        formData.delay !== null &&
                                                        formData.duration !== null &&
                                                        formData.maxPrebookPerDriver !== null
                                                            ? 'secondary'
                                                            : 'inherit'
                                                    }
                                                >
                                                    <Icon fontSize="small">add</Icon>
                                                </Button>
                                            </TooltipButton>
                                        </div>
                                    </div>
                                    <h2 style={{ display: 'flex' }}>{t('details.sumup')}</h2>
                                    <Listing
                                        header={
                                            <div style={{ display: 'flex', marginRight: 20, marginLeft: 20 }}>
                                                {[
                                                    { key: 'hour_from', upper: true },
                                                    { key: 'hour_to', upper: true },
                                                    { key: 'hours.form.delay' },
                                                    { key: 'hours.form.duration' },
                                                    { key: 'hours.form.maxPrebookPerDriver' },
                                                ].map(({ key, upper }) => (
                                                    <b key={key} style={{ flex: 1 }}>
                                                        {upper ? StringUtils.upperFirst(t(`common:${key}`)) : t(key)}
                                                    </b>
                                                ))}
                                                <div style={{ width: 46 }} />
                                            </div>
                                        }
                                        datas={planning?.time_slots?.hours?.map(
                                            (
                                                {
                                                    maxPrebookPerDriver,
                                                    rideDate: { start, end },
                                                    visibility: { delay, duration },
                                                },
                                                index,
                                            ) => {
                                                return (
                                                    <div
                                                        key={`${maxPrebookPerDriver} ${delay} ${duration} ${start} ${end}`}
                                                        style={{
                                                            flex: 'inherit',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {[
                                                            { value: start },
                                                            { value: end },
                                                            { value: StringUtils.formatTimeNumber(delay) },
                                                            { value: StringUtils.formatTimeNumber(duration) },
                                                            { value: maxPrebookPerDriver },
                                                        ].map(({ value }, index) => (
                                                            <div
                                                                key={value + ` ${index}`}
                                                                style={{ marginRight: 6, flex: 1 }}
                                                            >
                                                                <span>{value}</span>
                                                            </div>
                                                        ))}
                                                        <TooltipButton title={t('common:delete')}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={handleDelete(index)}
                                                                className={clsx(
                                                                    globalClasses.btnAutoWidth,
                                                                    globalClasses.btnDanger,
                                                                )}
                                                            >
                                                                <Icon fontSize="small">delete</Icon>
                                                            </Button>
                                                        </TooltipButton>
                                                    </div>
                                                );
                                            },
                                        )}
                                    />
                                </div>
                            );
                        },
                    },
                ],
            },
        ];
        return inputs;
    };

    const renderRemovePrebookPlanning = (): JSX.Element => {
        const prebook = prebooks[match.params.id];

        if (!prebook || !params.planning_id) {
            return null;
        }

        const willDelete = prebook.plannings.length > 1;
        return (
            <Dialog
                btnTitle={willDelete ? t('common:validate') : t('common:impossible')}
                description={
                    willDelete
                        ? t('common:deleteParam', {
                              id: `(${params.planning_id})`,
                          })
                        : t('remove_impossible')
                }
                customButton={
                    <TooltipButton title={t('common:deleteForever')}>
                        <Button
                            startIcon={<Icon>delete_forever</Icon>}
                            variant="contained"
                            className={clsx(globalClasses.roundButton, globalClasses.btnDanger)}
                            color="inherit"
                        >
                            {t('common:delete')}
                        </Button>
                    </TooltipButton>
                }
                noKOButton={!willDelete}
                onOK={willDelete ? handleDeletePlanning : () => {}}
            />
        );
    };

    let backRoute = `${paths.prebooks}?prebook_id=${match.params.id}`;
    if (params.planning_id) {
        backRoute = `${backRoute}&planning_id=${params.planning_id}`;
    }

    return (
        <App needRights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}>
            <ErrorMessage payload={crudPrebookError} />
            {(!params.id || (formData && formData.id)) && (
                <>
                    <form onSubmit={handleSubmitHours} id="form_add_hours">
                        <input type="submit" hidden value="Ok" />
                    </form>
                    <EnhancedForm
                        payload={formData}
                        inputs={generateInputs()}
                        backRoute={backRoute}
                        onSubmit={handleSubmit}
                        loader={isSavingPrebook}
                        onRemove={renderRemovePrebookPlanning()}
                    />
                </>
            )}
        </App>
    );
};

const PrebooksAddPlanningPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['prebook', 'common'])(PrebooksAddPlanningPage),
);

const mapStateToProps = (state) => ({
    prebooks: prebookSelectors.prebooks(state),
    crudPrebookError: errorsSelectors.crudPrebookError(state),
    isSavingPrebook: pendingSelectors.isPending(state, prebookTypes.UPDATE_PREBOOK),
});

const mapDispatchToProps = {
    getPrebook: prebookActions.getPrebook,
    updatePrebook: prebookActions.updatePrebook,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrebooksAddPlanningPageWithStyleAndTranslation);
